import {
    RuleConditionAmount,
    RuleConditionAmountRounding,
    RuleConditionType,
    TransactionRule,
    TransactionRuleCondition,
    TransactionRuleConditionDefinition,
    TransferRuleDirection,
} from "../../types/transactionRule";
import { ParsedSimilarToValue } from "../split-aware-description.helper";

export type RuleConditionValue = TransactionRuleConditionDefinition["value"];

export interface RuleScopeContext {
    /** financial account id -> entity id (null when the account has no entity). */
    readonly financialAccountEntityId: ReadonlyMap<number, number | null>;
    /** subaccount id -> financial account id. */
    readonly subaccountFinancialAccountId: ReadonlyMap<string, number>;
}

export interface RuleWarnings {
    isUnreachable: boolean;
    isDuplicate: boolean;
}

export const EMPTY_RULE_WARNINGS: RuleWarnings = {
    isUnreachable: false,
    isDuplicate: false,
};

export interface IndexedCondition {
    readonly condition: TransactionRuleCondition;
    readonly values: readonly string[];
    readonly scopeEntities?: ReadonlySet<number>;
    readonly scopeAccounts?: ReadonlySet<number>;
    readonly scopeSubaccounts?: ReadonlySet<string>;
    readonly categoryRaw?: ReadonlySet<string>;
    readonly similarTo?: ParsedSimilarToValue;
}

export interface IndexedRule {
    readonly rule: TransactionRule;
    readonly buckets: ReadonlyMap<string, readonly IndexedCondition[]>;
    readonly conditionBucketKeys: ReadonlySet<string>;
}

export const SCOPE_TYPES: ReadonlySet<RuleConditionType> = new Set([
    RuleConditionType.ENTITY,
    RuleConditionType.FINANCIAL_ACCOUNTS,
    RuleConditionType.SUBACCOUNTS,
]);

export const CATEGORY_TYPES: ReadonlySet<RuleConditionType> = new Set([
    RuleConditionType.CATEGORY,
    RuleConditionType.SUGGESTED_CATEGORY,
]);

export const ARRAY_SET_TYPES: ReadonlySet<RuleConditionType> = new Set([
    RuleConditionType.DESCRIPTION_IS,
    RuleConditionType.DESCRIPTION_INCLUDES,
    RuleConditionType.DESCRIPTION_DOES_NOT_INCLUDE,
    RuleConditionType.COUNTERPARTY,
    RuleConditionType.CLASS,
]);

export const ROUNDING_TYPES: ReadonlySet<RuleConditionType> = new Set([
    RuleConditionType.AMOUNT_IS_ROUND,
    RuleConditionType.AMOUNT_IS_NOT_ROUND,
]);

/** Delimiter used to build composite bucket keys. Uses a character outside
 *  the printable ASCII range used by enum values so it can never collide. */
const BUCKET_KEY_DELIMITER = "\0";

export function bucketKey(
    conditionType: RuleConditionType,
    direction: TransferRuleDirection | null,
): string {
    return `${conditionType}${BUCKET_KEY_DELIMITER}${direction ?? ""}`;
}

const RULE_CONDITION_TYPE_VALUES: readonly string[] =
    Object.values(RuleConditionType);
const TRANSFER_RULE_DIRECTION_VALUES: readonly string[] = Object.values(
    TransferRuleDirection,
);

function isRuleConditionType(s: string): s is RuleConditionType {
    return RULE_CONDITION_TYPE_VALUES.includes(s);
}

function isTransferRuleDirection(s: string): s is TransferRuleDirection {
    return TRANSFER_RULE_DIRECTION_VALUES.includes(s);
}

export function parseBucketKey(
    key: string,
): { type: RuleConditionType; direction: TransferRuleDirection | null } | null {
    const [typeRaw, dirRaw] = key.split(BUCKET_KEY_DELIMITER);
    if (typeRaw === undefined || !isRuleConditionType(typeRaw)) {
        return null;
    }
    if (dirRaw === "" || dirRaw === undefined) {
        return { type: typeRaw, direction: null };
    }
    if (!isTransferRuleDirection(dirRaw)) {
        return null;
    }
    return { type: typeRaw, direction: dirRaw };
}

export function normalizeValueArray(
    value: RuleConditionValue | undefined,
): readonly string[] {
    if (Array.isArray(value)) {
        return value.map(String);
    }
    if (value === undefined || value === null) {
        return [];
    }
    if (typeof value === "object") {
        return [];
    }
    return [String(value)];
}

export function areSetsEqual<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
    if (a.size !== b.size) {
        return false;
    }
    for (const v of a) {
        if (!b.has(v)) {
            return false;
        }
    }
    return true;
}

export function isSetSubset<T>(
    subset: ReadonlySet<T>,
    superset: ReadonlySet<T>,
): boolean {
    if (subset.size > superset.size) {
        return false;
    }
    for (const v of subset) {
        if (!superset.has(v)) {
            return false;
        }
    }
    return true;
}

export function isRoundingValue(
    value: RuleConditionValue | undefined,
): value is RuleConditionAmountRounding {
    return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        "level" in value &&
        "isCredit" in value
    );
}

export function isAmountValue(
    value: RuleConditionValue | undefined,
): value is RuleConditionAmount {
    return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        !isRoundingValue(value)
    );
}

export function isDateValue(value: RuleConditionValue | undefined): value is {
    after?: string;
    before?: string;
} {
    return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        !isRoundingValue(value) &&
        ("after" in value || "before" in value)
    );
}

export function areRoundingValuesEqual(
    a: RuleConditionAmountRounding,
    b: RuleConditionAmountRounding,
): boolean {
    return a.level === b.level && a.isCredit === b.isCredit;
}

export function isSimilarToValueSubset(
    lower: ParsedSimilarToValue,
    higher: ParsedSimilarToValue,
): boolean {
    if (lower.normalizedDescriptionId !== higher.normalizedDescriptionId) {
        return false;
    }
    // An undefined key is a legacy whole-cluster target, so it covers every split scope.
    return (
        higher.effectiveSplitKey === undefined ||
        higher.effectiveSplitKey === lower.effectiveSplitKey
    );
}

export function areSimilarToValuesEqual(
    a: ParsedSimilarToValue,
    b: ParsedSimilarToValue,
): boolean {
    return (
        a.normalizedDescriptionId === b.normalizedDescriptionId &&
        a.effectiveSplitKey === b.effectiveSplitKey
    );
}
